import { Component, inject, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { finalize, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthAdaptorService, AuthStateAdaptorService } from '@aiwyn/auth';
import { PaymentMethodType, PaymentMethodValidationError } from '@aiwyn/api';
import {
  compareStringsIgnoreCase,
  extractFailureContext,
  isEmpty,
} from '@aiwyn/shared';
import {
  NewPaymentMethodDialogComponent,
  NewPaymentMethodDialogData,
  PAYMENT_METHOD_BRAND_DISPLAY,
  PaymentMethodDeletionBlockedError,
  PaymentMethodIntent,
  PaymentMethodSurface,
  PaymentMethodTrackService,
  PaymentMethodWorkflowEvent,
  PaymentPlatformService,
  PlatformBankPaymentMethod,
  PlatformCardPaymentMethod,
} from '@aiwyn/common/payments';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CannotDeletePaymentMethodComponent } from '../../modals/cannot-delete-payment-method/cannot-delete-payment-method.component';
import { ConfirmDeleteComponent, ConfirmDeleteComponentData } from '../../modals/confirm-delete/confirm-delete.component';

@Component({
  selector: 'app-saved-payment-methods',
  templateUrl: './saved-payment-methods.component.html',
  styleUrls: ['./saved-payment-methods.component.scss'],
  standalone: false,
  providers: [PaymentMethodTrackService],
})
export class SavedPaymentMethodsComponent implements OnDestroy {
  /**
   * Every PAYMENT_METHOD_* event fired from this settings page (and from the Confirm-delete /
   * New-payment-method dialogs opened by it) is attributed to this surface.
   */
  private static readonly SURFACE: PaymentMethodSurface = 'settings-payment-methods';

  PAYMENT_METHOD_BRAND_DISPLAY = PAYMENT_METHOD_BRAND_DISPLAY;

  // Subscriptions
  private readonly _unsubscribe$ = new Subject<void>();

  private readonly paymentMethodTrackService = inject(PaymentMethodTrackService);
  private readonly authStateAdaptor = inject(AuthStateAdaptorService);

  constructor(
    readonly authAdaptor: AuthAdaptorService,
    private readonly dialog: MatDialog,
    private readonly paymentPlatformService: PaymentPlatformService,
  ) {
    this.paymentMethodTrackService.setSurface(SavedPaymentMethodsComponent.SURFACE);
    this.paymentMethodTrackService.setUserType(this.authStateAdaptor.getUserTypeSnapshot());
  }

  defaultPaymentMethodId$ = this.paymentPlatformService.getDefaultPaymentMethodId();

  savedCardPaymentMethods$: Observable<PlatformCardPaymentMethod[]> = this.paymentPlatformService.getSavedPaymentMethods().pipe(
    map(savedPaymentMethods => {
      if (isEmpty(savedPaymentMethods)) {
        return [];
      }

      return savedPaymentMethods
        .filter(({ type }) => type === PaymentMethodType.CARD)
        .sort((a, b) => compareStringsIgnoreCase(a.sortKey, b.sortKey)) as PlatformCardPaymentMethod[];
    }),
    shareReplay(1),
  );

  cardValidationErrorMessagesMap$: Observable<Record<string, string[]>> = this.savedCardPaymentMethods$.pipe(
    switchMap(savedCardPaymentMethods => {
      if (isEmpty(savedCardPaymentMethods)) {
        return of({});
      }

      return combineLatest(
        savedCardPaymentMethods.map(savedCardPaymentMethod =>
          this.paymentPlatformService.validatePaymentMethod(savedCardPaymentMethod).pipe(
            map(validationErrors => {
              const validationErrorMessages = Object.keys(validationErrors ?? {})
                .filter(errorKey => errorKey !== PaymentMethodValidationError.DUPLICATE)
                .map(errorKey => errorKey === PaymentMethodValidationError.EXPIRATION ? 'Card expired' : 'No longer accepted');

              return isEmpty(validationErrorMessages) ? {} : { [savedCardPaymentMethod.id]: validationErrorMessages };
            }),
          ),
        ),
      ).pipe(
        map(validationErrorsArray => Object.assign({}, ...validationErrorsArray)),
      );
    }),
  );

  savedAchPaymentMethods$: Observable<PlatformBankPaymentMethod[]> = this.paymentPlatformService.getSavedPaymentMethods().pipe(
    map(savedPaymentMethods =>
      savedPaymentMethods
        .filter(({ type }) => type !== PaymentMethodType.CARD)
        .sort((a, b) => compareStringsIgnoreCase(a.sortKey, b.sortKey)) as PlatformBankPaymentMethod[],
    ),
    shareReplay(1),
  );

  /**
   * Launch modal for creating new payment method.
   */
  newPaymentMethodDialog: MatDialogRef<NewPaymentMethodDialogComponent>;
  // `newPaymentMethodDialog` is only assigned once the async pipeline below emits, so it alone cannot guard against rapid
  // repeat clicks — two clicks in quick succession opened two stacked dialogs, whose duplicate Stripe elements then threw
  // IntegrationError (PP-3625, PP-3574). This flag closes that window synchronously.
  private _isNewPaymentMethodDialogPipelineActive = false;
  openNewPaymentMethodModal(): void {
    if (this._isNewPaymentMethodDialogPipelineActive || this.newPaymentMethodDialog != undefined) {
      return;
    }
    this._isNewPaymentMethodDialogPipelineActive = true;

    combineLatest([
      this.paymentPlatformService.getSavedPaymentMethods().pipe(map(({ length }) => length > 0)),
      this.paymentPlatformService.getPaymentMethodMode(),
      this.paymentPlatformService.setSelectedBusinessEntityId(undefined), // reset selected business entity ID to use default setup intent
    ]).pipe(
      take(1),
      switchMap(([userHasSavedPaymentMethods, existingPaymentMethodMode]) => {
        this.newPaymentMethodDialog = this.dialog.open<NewPaymentMethodDialogComponent, NewPaymentMethodDialogData>(
          NewPaymentMethodDialogComponent,
          {
            data: {
              userHasSavedPaymentMethods,
              intent: PaymentMethodIntent.SETUP,
              surface: SavedPaymentMethodsComponent.SURFACE,
            },
            disableClose: true,
          },
        );

        return this.newPaymentMethodDialog.afterClosed().pipe(
          switchMap(_ => combineLatest([
            this.paymentPlatformService.removeNewBillingDetails(),
            this.paymentPlatformService.setPaymentMethodMode(existingPaymentMethodMode),
          ])),
        );
      }),
      take(1),
      takeUntil(this._unsubscribe$),
      finalize(() => {
        this.newPaymentMethodDialog = undefined;
        this._isNewPaymentMethodDialogPipelineActive = false;
      }),
    ).subscribe();
  }

  /**
   * Remove Payment Method.
   */
  removePaymentMethod(isAch: boolean, paymentMethodId: string): void {
    const paymentMethodType = isAch ? 'ACH' : 'CARD';
    this.paymentMethodTrackService.trackWorkflow(
      PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_STARTED,
      { paymentMethodId, paymentMethodType },
    );

    this.dialog.open<ConfirmDeleteComponent, ConfirmDeleteComponentData>(ConfirmDeleteComponent, {
      data: { isAch, paymentMethodId, paymentMethodType, surface: SavedPaymentMethodsComponent.SURFACE },
    }).afterClosed().pipe(
      switchMap(result => {
        if (result instanceof PaymentMethodDeletionBlockedError) {
          return this.dialog.open(CannotDeletePaymentMethodComponent, {
            data: {
              isUsedByAutomaticPayments: result.isUsedByAutomaticPayments,
              isUsedByEngagements: result.isUsedByEngagements,
              isUsedByPendingPayments: result.isUsedByPendingPayments,
            },
          }).afterClosed();
        }
        return of(undefined);
      }),
      take(1),
    ).subscribe();
  }

  routeToDebitBlockInfoPage(): void {
    window.open('https://www.clientsupport.aiwyn.ai/hc/en-us/articles/6467585256091-Failed-ACH-Payment', '_blank');
  }

  setPaymentMethodAsDefault(paymentMethodId: string, paymentMethodType: string): void {
    const operationKey = `PAYMENT_METHOD_SET_DEFAULT:${paymentMethodId}`;
    this.paymentMethodTrackService.startOperation(operationKey);

    this.paymentPlatformService.setDefaultPaymentMethod(paymentMethodId).pipe(
      tap({
        next: _ => this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED,
          {
            paymentMethodId,
            paymentMethodType,
            operationDurationMs: this.paymentMethodTrackService.endOperation(operationKey),
          },
        ),
        error: error => this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_FAILED,
          {
            ...extractFailureContext(error),
            paymentMethodId,
            paymentMethodType,
            operationDurationMs: this.paymentMethodTrackService.endOperation(operationKey),
          },
        ),
      }),
      take(1),
    ).subscribe();
  }

  /**
   * Destroy Subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
